import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR, formatDate, daysUntil, API } from "../lib/api";
import Layout, { Card, Badge, Spinner } from "../components/Layout";
import { HelpInline } from "../components/HelpAndSearch";
import { Plus, MagnifyingGlass, FunnelSimple, ArrowRight, Database, Download, Upload, BookmarkSimple } from "@phosphor-icons/react";

const PORTALS = ["CONSIP", "MEPA", "MIT", "TED", "EMPULIA", "SINTEL"];

export default function Scouting() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [portal, setPortal] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (portal) params.portal = portal;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/tenders", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [portal, statusFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const seed = async () => {
    await api.post("/seed");
    load();
  };

  return (
    <Layout
      title="Scouting Gare"
      subtitle="Feed multi-portale (CONSIP, MePA, MIT, TED, portali regionali)"
      actions={
        <>
          <button onClick={async () => {
            try {
              const { data } = await api.post("/scrapers/ted", { cpv: "72000000", limit: 5 });
              alert(`TED EU scraper: ${data.created || 0} gare importate. ${data.error || data.source || ""}`);
              load();
            } catch(e) { alert("Errore TED: " + e.message); }
          }} className="btn-secondary" data-testid="btn-ted">
            <Download size={14} className="inline mr-1.5" /> Import TED EU
          </button>
          <button onClick={async () => {
            if (!confirm("Cancellera TUTTE le gare non scaricate da fonti reali (TED). Procedere?")) return;
            const { data } = await api.post("/admin/wipe-demo-tenders");
            alert(`Cancellate ${data.deleted_demo_tenders} gare demo/manuali. Lasciate solo gare reali.`);
            load();
          }} className="btn-secondary" data-testid="btn-wipe-demo">
            Solo Reali
          </button>
          <button onClick={seed} className="btn-secondary" data-testid="btn-seed">
            <Database size={14} className="inline mr-1.5" /> Seed Demo
          </button>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="btn-new-tender">
            <Plus size={14} className="inline mr-1.5" /> Importa Gara
          </button>
        </>
      }
    >
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px] relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="scouting-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per titolo, oggetto..."
              className="input-field pl-9"
            />
          </div>
          <select data-testid="filter-portal" value={portal} onChange={(e) => setPortal(e.target.value)} className="input-field max-w-[160px]">
            <option value="">Tutti i portali</option>
            {PORTALS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field max-w-[180px]">
            <option value="">Tutti gli stati</option>
            <option value="NEW">Nuova</option>
            <option value="INTERESTING">Interessante</option>
            <option value="IN_ANALYSIS">In analisi</option>
            <option value="ARCHIVED">Archiviata</option>
          </select>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto" data-testid="scouting-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <Th>Gara</Th>
                <Th>Stazione</Th>
                <Th>Portale</Th>
                <Th className="text-right">Importo</Th>
                <Th>Scadenza</Th>
                <Th>Fattibilita</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center"><Spinner /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-500 text-sm">
                  Nessuna gara. Premi <strong>Seed Demo</strong> per popolare con dati di esempio.
                </td></tr>
              ) : items.map((t) => {
                const d = daysUntil(t.deadline_at);
                const urgent = d <= 7;
                return (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors" onClick={() => nav(`/tenders/${t.id}`)} data-testid={`tender-row-${t.id}`}>
                    <td className="px-4 py-3 max-w-md">
                      <div className="font-medium truncate">{t.title}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">CIG {t.cig} • {t.procedure_type}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{t.station_name}</td>
                    <td className="px-4 py-3"><span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded-sm">{t.portal}</span></td>
                    <td className="px-4 py-3 text-right font-mono">{formatEUR(t.base_amount)}</td>
                    <td className="px-4 py-3">
                      <div className={`text-xs ${urgent ? "text-red-600 font-semibold" : "text-zinc-700"}`}>{formatDate(t.deadline_at)}</div>
                      <div className="text-[10px] text-zinc-500">{d}gg</div>
                    </td>
                    <td className="px-4 py-3">
                      {t.feasibility_label ? (
                        <Badge kind={t.feasibility_label.toLowerCase()} testid={`feas-${t.id}`}>{t.feasibility_label}</Badge>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                    <td className="px-4 py-3"><ArrowRight size={14} className="text-zinc-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {showNew && <NewTenderModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </Layout>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left label-up text-zinc-600 ${className}`}>{children}</th>;
}

function NewTenderModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    title: "", station_name: "", portal: "MEPA", procedure_type: "APERTA",
    award_criterion: "OEPV", base_amount: 0, deadline_at: "", publication_date: new Date().toISOString().slice(0, 10),
    cig: "", description: "", raw_text: "", cpv_codes: "", url: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tenders", {
        ...f,
        base_amount: parseFloat(f.base_amount),
        deadline_at: new Date(f.deadline_at).toISOString(),
        publication_date: new Date(f.publication_date).toISOString(),
        cpv_codes: f.cpv_codes.split(",").map(s => s.trim()).filter(Boolean),
      });
      onCreated();
    } finally { setSaving(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="modal-new-tender">
      <form onSubmit={submit} className="bg-white border border-zinc-300 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <h2 className="font-display text-2xl font-extrabold tracking-tighter">Importa Gara</h2>
        <p className="text-sm text-zinc-500 -mt-3">Inserisci i dati base e il testo del disciplinare per l'analisi AI.</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Titolo *" colSpan className="col-span-2"><input className="input-field" required value={f.title} onChange={upd("title")} data-testid="new-title" /></Field>
          <Field label="Stazione Appaltante *"><input className="input-field" required value={f.station_name} onChange={upd("station_name")} data-testid="new-station" /></Field>
          <Field label="Portale">
            <select className="input-field" value={f.portal} onChange={upd("portal")} data-testid="new-portal">
              {PORTALS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="CIG"><input className="input-field" value={f.cig} onChange={upd("cig")} data-testid="new-cig" /></Field>
          <Field label="Importo (EUR) *"><input type="number" className="input-field" required value={f.base_amount} onChange={upd("base_amount")} data-testid="new-amount" /></Field>
          <Field label="Procedura"><select className="input-field" value={f.procedure_type} onChange={upd("procedure_type")}>
            {["APERTA", "RISTRETTA", "NEGOZIATA", "MEPA_RDO"].map(p => <option key={p}>{p}</option>)}
          </select></Field>
          <Field label="Criterio"><select className="input-field" value={f.award_criterion} onChange={upd("award_criterion")}>
            <option>OEPV</option><option>PREZZO</option>
          </select></Field>
          <Field label="Data pubblicazione"><input type="date" className="input-field" value={f.publication_date} onChange={upd("publication_date")} /></Field>
          <Field label="Scadenza *"><input type="datetime-local" required className="input-field" value={f.deadline_at} onChange={upd("deadline_at")} data-testid="new-deadline" /></Field>
          <Field label="CPV (separati da virgola)" colSpan className="col-span-2"><input className="input-field" value={f.cpv_codes} onChange={upd("cpv_codes")} placeholder="72200000-7, 72500000-0" /></Field>
          <Field label="URL della gara sul portale (opzionale)" colSpan className="col-span-2">
            <input type="url" className="input-field font-mono text-xs" value={f.url || ""} onChange={upd("url")} placeholder="https://acquistinretepa.it/... oppure https://ted.europa.eu/..." data-testid="new-url" />
          </Field>
          <Field label="Descrizione breve" colSpan className="col-span-2"><textarea className="input-field" rows={2} value={f.description} onChange={upd("description")} /></Field>
          <Field label="Testo disciplinare (per analisi AI)" colSpan className="col-span-2">
            <textarea className="input-field font-mono text-xs" rows={6} value={f.raw_text} onChange={upd("raw_text")} placeholder="Incolla qui il testo del disciplinare di gara..." data-testid="new-raw-text" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="new-cancel">Annulla</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="new-save">{saving ? "Salvataggio..." : "Crea Gara"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="label-up mb-1">{label}</div>
      {children}
    </div>
  );
}
